/*
 * Copyright © 2018-2026, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import { FormProvider, useForm } from "react-hook-form";
import Chart from "react-apexcharts";
import moment from "moment";
import { strings } from "../../common/Strings/Strings";
import { InvoiceStatisticsResponse } from "../../models/invoice/InvoiceStatisticsResponse";
import { useClinic } from "../../contexts/ClinicContext";
import ClinicInvoiceApi from "../../api/ClinicInvoiceApi";
import { getGeneralError } from "../../util/helperFunctions";
import NumberFormatter from "../../util/NumberFormatter";
import { SearchInvoices } from "../../models/apiparams/SearchInvoices";
import DateFilters, { DateTemplate } from "../../components/DateFilters";
import { CountryDetailsResponse } from "../../models/management/CountryDetailsResponse";
import LoaderInline from "../../components/LoaderInline";
import { ArrowDownTray } from "../../common/Icons/ArrowDownTray";
import Button from "../../components/Button";
import { downloadPaymentSummaryOfClinic, printPaymentSummaryOfClinic } from "../../util/FileUtils";
import AlertBox, { AlertType } from "../../components/AlertBox";
import Tooltip from "../../components/Tooltip";
import { Printer } from "../../common/Icons/Printer";
import logger from "../../util/logger";
import StatInfoTile from "./StatInfoTileComponent";

interface Props {
  countryDetails?: CountryDetailsResponse;
}

interface ApexChart {
  colors?: string[];
  labels: string[];
  legend?: {
    itemMargin: any;
    markers: any;
    position?: "top" | "right" | "bottom" | "left";
    labels: any;
  };
  dataLabels: any;
  series: number[];
}

const Income = ({ countryDetails }: Props): ReactElement => {
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<InvoiceStatisticsResponse>();
  const [pieValue, setPieValues] = useState<ApexChart>();
  const [loading, setLoading] = useState<boolean>(true);
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const [reloadStatistics, setReloadStatistics] = useState<boolean>(false);
  const [isTotalEmptyMessage, setIsTotalEmptyMessage] = useState<boolean>(false);

  const filterFormMethods = useForm<SearchInvoices>({
    mode: "onChange",
    defaultValues: {
      endDate: new Date(),
      startDate: new Date(),
      dateTemplate: DateTemplate.today,
    },
  });
  const { getValues: getStatisticsFilterFormValues } = filterFormMethods;

  const { clinic } = useClinic();

  useEffect(() => {
    const getStatistics = async () => {
      try {
        setLoading(true);
        let response: AxiosResponse<InvoiceStatisticsResponse>;
        const request = getStatisticsFilterFormValues();

        if (clinic) {
          response = await ClinicInvoiceApi.getPaymentSummary(clinic.id, request);
        } else {
          return;
        }

        setError(null);
        setStatistics(response.data);

        const series: number[] = [];
        const labels: string[] = [];
        const { cash, card, transfer, directDebit, online, insurance, other } = response.data;

        if (cash > 0) {
          series.push(cash);
          labels.push(strings.cashIncome);
        }
        if (card > 0) {
          series.push(card);
          labels.push(strings.cardIncome);
        }
        if (transfer > 0) {
          series.push(transfer);
          labels.push(strings.transferIncome);
        }
        if (directDebit > 0) {
          series.push(directDebit);
          labels.push(strings.directDebitIncome);
        }
        if (online > 0) {
          series.push(online);
          labels.push(strings.onlineIncome);
        }
        if (insurance > 0) {
          series.push(insurance);
          labels.push(strings.insuranceIncome);
        }
        if (other > 0) {
          series.push(other);
          labels.push(strings.other);
        }

        const isDark = document.documentElement.classList.contains("dark");
        setPieValues({
          series,
          labels,
          colors: ["#0CA2FE", "#9edaff", "#64748b", "#cbd5e1", "#01619d", "#334155", "#e6f5ff"],
          legend: {
            itemMargin: {
              horizontal: 12, // space between items in a row
              vertical: 8, // space between rows
            },
            markers: {
              offsetX: -6, // negative = closer to text, positive = farther
            },
            position: "bottom",
            labels: {
              colors: isDark ? "fff" : "#111827",
            },
          },
          dataLabels: {
            style: {
              fontSize: "14px",
              fontWeight: "bold",
              colors: ["#fff"],
            },
            dropShadow: {
              enabled: false,
            },
          },
        });
      } catch (err) {
        setError(await getGeneralError(err));
      } finally {
        setLoading(false);
      }
    };

    void getStatistics();
  }, [reloadStatistics, getStatisticsFilterFormValues, clinic]);

  const printOrDownload = async (isDownload: boolean) => {
    setIsTotalEmptyMessage(false);
    if (statistics?.total === 0) {
      setIsTotalEmptyMessage(true);
      return;
    }
    if (clinic) {
      setPdfLoading(true);
      const startDate = moment(getStatisticsFilterFormValues("startDate")).format("YYYY-MM-DD");
      const endDate = moment(getStatisticsFilterFormValues("endDate")).format("YYYY-MM-DD");
      try {
        isDownload
          ? await downloadPaymentSummaryOfClinic(clinic.id, startDate, endDate)
          : await printPaymentSummaryOfClinic(clinic.id, startDate, endDate, () => {
              logger.info("Printing");
            });
      } catch (err) {
        logger.error(err);
        setError(await getGeneralError(err));
      } finally {
        setPdfLoading(false);
      }
    } else {
      logger.error("No clinic.");
    }
  };

  return (
    <div className="w-full md:max-w-4xl space-y-6">
      <div className="flex">
        <h2 className="text-lg font-semibold leading-tight text-zinc-800 lg:text-xl dark:text-white">
          {strings.incomeSummary}
        </h2>
        <div className="ml-auto mr-4">
          <Tooltip content={strings.downloadIncomeSummary} placement="top">
            <Button className="tw-link" disabled={pdfLoading} onClick={() => printOrDownload(true)} variant="icon">
              <ArrowDownTray />
            </Button>
          </Tooltip>
        </div>
        <div>
          <Tooltip content={strings.printIncomeSummary} placement="top">
            <Button className="tw-link" disabled={pdfLoading} onClick={() => printOrDownload(false)} variant="icon">
              <Printer />
            </Button>
          </Tooltip>
        </div>
      </div>
      <AlertBox
        closeAble={false}
        hidden={!isTotalEmptyMessage}
        message={strings.totalIncomeIsZeroInfo}
        type={AlertType.INFO}
      />
      <FormProvider {...filterFormMethods}>
        <form>
          <DateFilters labelOff onChange={() => setReloadStatistics(!reloadStatistics)} />
        </form>
      </FormProvider>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatInfoTile
          value={statistics && NumberFormatter.formatPrice(statistics?.total, countryDetails?.currency)}
          title={strings.revenue}
        />
        <StatInfoTile
          value={statistics && NumberFormatter.formatPrice(statistics?.unpaid, countryDetails?.currency)}
          title={strings.outstandingBalance}
        />
        <StatInfoTile
          value={statistics && NumberFormatter.formatPrice(statistics?.pending, countryDetails?.currency)}
          title={strings.readyForPaymentBalance}
        />
      </div>
      <AlertBox message={error} />
      <div>
        {loading ? (
          <LoaderInline />
        ) : (
          <div className="tw-card p-6 md:w-1/2">
            <div>{strings.paymentMethod}</div>
            {pieValue?.series && pieValue?.series?.length > 0 ? (
              <Chart options={pieValue} series={pieValue.series} type="pie" />
            ) : (
              strings.noPaymentMethodHistoryInfo
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Income;
